import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import useForm from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid, Box } from '@material-ui/core';
import { getCarManufacturerModels } from '../../../../../redux/modules/carMarks';
import ClaimApplicantDetails from '../../../../../components/FormComponents/ClaimApplicantDetails/ClaimApplicantDetails';
import { ACCIDENT_PARTICIPANT_TYPES, LEGAL_PERSONAL_INFORMATION_FIELDS } from '../../../options';
import ClaimParticipantDetails from '../../../../../components/FormComponents/ClaimParticipantDetails/ClaimParticipantDetails';
import ApplicantResidenceAddress from '../ApplicantResidenceAddress';
import TextInputFieldWithValidation from '../../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import SelectWithValidation from '../../../../../components/ValidationFormElements/SelectWithValidation';
import ChangeApplicant from '../../../../../components/ClaimFormComponents/ChangeApplicant';
import OtherLegalApplicantForm from '../../../../../components/FormComponents/OtherLegalApplicantForm';
import ChangeParticipant from '../../../../../components/ClaimFormComponents/ChangeParticipant';
import {
  changeParticipant,
  filterTransportManufacturer,
  filterTransportModel,
} from '../../../../../helpers/claims';
import useRegisterValidationFields from '../../../../../hooks/useRegisterValidationFields';
import useUnRegisterValidationFields from '../../../../../hooks/useUnRegisterValidationFields';
import ClaimStepNavigationWrapper from '../../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import useDeviceResize from '../../../../../hooks/useDeviceResize';
import legalSchema from '../../../../../validations/claimValidationSchema/casco/legalPersonalSchema';
import { LANGUAGES } from '../../../../../constants';
import usePrevious from '../../../../../hooks/usePrevious';
import Loader from '../../../../../components/Loaders/Loader';
import FormSection from '../../../../../components/FormComponents/FormSection';
import { APPLICANT_TYPES } from '../../../../../constants/claim';

import UserInfoIcon from '../../../../../img/formSectionIcons/userInfo.svg';

const LegalPersonalInformation = ({
  initialData,
  onFormSubmit,
  formLanguage,
  setStepNavigationBox,
  goPrevious,
}) => {
  const isDeviceLgSize = useDeviceResize('lg');
  const { t } = useTranslation();

  const { transportManufacturer, transportModel, applicant } = initialData;

  const isSameOwner = !!+applicant.ownerSameInsured;
  const orgNameFromBack = isSameOwner
    ? applicant.organizationName
    : applicant.ownerOrganizationName;
  const hvhhFromBack = isSameOwner ? applicant.hvhh : applicant.ownerHvhh;

  const resCountry = isSameOwner ? applicant.country : applicant.ownerCountry;
  const resState = isSameOwner ? applicant.state : applicant.ownerState;
  const resCityVillage = isSameOwner ? applicant.cityVillage : applicant.ownerCityVillage;
  const resStreet = isSameOwner ? applicant.street : applicant.ownerStreet;

  const dispatch = useDispatch();
  const countries = useSelector((store) => store.countriesReducer.countries);

  const {
    manufacturers,
    manufacurerModels,
    isManufacturersLoading,
    isManufacturerModelsLoading,
  } = useSelector((store) => store.carMarks, shallowEqual);

  const [manufacturer, setManufacturer] = useState(null);
  const [model, setModel] = useState(null);
  const [isModelInitialized, setIsModelInitialized] = useState(false);
  const [participantIs, setParticipantIs] = useState(
    initialData.participantIs || ACCIDENT_PARTICIPANT_TYPES[0].value,
  );
  const [applicantIs, setApplicantIs] = useState(
    initialData.applicantIs || ACCIDENT_PARTICIPANT_TYPES[0].value,
  );
  const previousApplicantState = usePrevious(applicantIs);

  const {
    handleSubmit,
    register,
    setValue,
    errors,
    clearError,
    unregister,
    getValues,
    triggerValidation,
    watch,
  } = useForm({
    validationSchema: legalSchema,
    mode: 'onBlur',
    defaultValues: initialData ? { ...initialData } : {},
  });

  // These two fields are nested and here used watch to trigger each changes
  watch(['applicantResidenceCountry', 'participantCitizenship']);
  useRegisterValidationFields(LEGAL_PERSONAL_INFORMATION_FIELDS, register);
  useUnRegisterValidationFields(LEGAL_PERSONAL_INFORMATION_FIELDS, unregister);

  const [stateNumber, setStateNumber] = useState(getValues().stateNumber);
  const [vin, setVin] = useState(getValues().vin);
  const { applicantResidenceCountry, participantCitizenship, organizationName } = getValues();

  const handleChange = (target) => {
    setValue(target.name, target.value, true);
  };

  const handleManufacturerChange = (m) => {
    if (!manufacturer || m.id !== manufacturer.id) {
      setManufacturer(m);
      setValue('transportManufacturer', m);
      setModel(null);
      setValue('transportModel', null);
      dispatch(getCarManufacturerModels(m.id));
    }
  };

  const handleModelChange = (m) => {
    setModel(m);
    setValue('transportModel', m, true);
  };

  const handleChangeParticipant = (value) => {
    changeParticipant(setParticipantIs, setValue, value, register, unregister);
  };

  const onSubmit = (data) => {
    onFormSubmit(data);
  };

  useEffect(() => {
    setValue('applicantIs', applicantIs);
  }, [setValue, applicantIs, applicantResidenceCountry]);
  useEffect(() => {
    setValue('participantIs', participantIs);
  }, [setValue, participantIs, participantCitizenship]);

  useEffect(() => {
    setValue('formLanguage', formLanguage);
  }, [formLanguage, setValue]);

  useEffect(() => {
    setValue('organizationName', organizationName, !!organizationName);
  }, [setValue, organizationName]);

  useEffect(() => {
    setValue('stateNumber', initialData.applicant.stateNumber, !!initialData.applicant.stateNumber);
    setValue('vin', initialData.applicant.vin, !!initialData.applicant.vin);
  }, [initialData.applicant.stateNumber, initialData.applicant.vin, setValue]);

  useEffect(
    () => filterTransportManufacturer(
      transportManufacturer,
      manufacturers,
      setValue,
      setManufacturer,
      dispatch,
    ),
    [transportManufacturer, manufacturers, dispatch, setValue],
  );

  useEffect(() => {
    filterTransportModel(
      transportModel,
      manufacurerModels,
      isModelInitialized,
      setValue,
      setModel,
      setIsModelInitialized,
    );
  }, [transportModel, manufacurerModels, isModelInitialized, setValue]);

  useEffect(() => {
    if (formLanguage === LANGUAGES.HY && !!orgNameFromBack) {
      setValue('organizationName', orgNameFromBack);
    }
  }, [formLanguage, orgNameFromBack, setValue]);

  useEffect(() => {
    setValue('hvhh', hvhhFromBack);
  }, [hvhhFromBack, setValue]);

  if (isManufacturersLoading || isManufacturerModelsLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  return (
    <Box>
      <ClaimStepNavigationWrapper
        clearError={clearError}
        errors={errors}
        isNextDisabled={false}
        handleSubmit={handleSubmit}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        onSubmit={onSubmit}
        back={goPrevious}
        dependencies={[JSON.stringify(errors), isDeviceLgSize, false]}
      />
      <FormSection title="claim.personalData.cascoAffectedRowLbl" icon={UserInfoIcon}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name="hvhh"
              labelKey="claim.personalData.hvhh"
              autoComplete="off"
              register={register}
              handleChange={({ target }) => handleChange(target)}
              disabled
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name="organizationName"
              labelKey="claim.personalData.name"
              autoComplete="off"
              register={register}
              handleChange={({ target }) => handleChange(target)}
              disabled={!!orgNameFromBack && formLanguage === LANGUAGES.HY}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name="stateNumber"
              labelKey="claim.personalData.stateNum"
              autoComplete="off"
              value={stateNumber}
              handleChange={({ target }) => {
                setStateNumber(target.value);
                handleChange(target);
              }}
              disabled={!!initialData.applicant.stateNumber}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectWithValidation
              name="transportManufacturer"
              label="claim.personalData.manufacturer"
              value={manufacturer || ''}
              formProps={{
                errors,
                clearError,
              }}
              getMenuLabel={(option) => option.name}
              options={manufacturers || []}
              onChange={(m) => handleManufacturerChange(m)}
              disabled={
                manufacturer && manufacturer.name === initialData.applicant.transportManufacturer
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SelectWithValidation
              name="transportModel"
              label="claim.personalData.model"
              value={model || ''}
              formProps={{
                errors,
                clearError,
              }}
              getMenuLabel={(option) => option.name}
              options={manufacurerModels || []}
              onChange={(newModel) => handleModelChange(newModel)}
              disabled={model && model.name === initialData.applicant.transportModel}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInputFieldWithValidation
              formProps={{ errors, clearError }}
              name="vin"
              labelKey="claim.personalData.vin"
              autoComplete="off"
              value={vin}
              handleChange={({ target }) => {
                setVin(target.value);
                handleChange(target);
              }}
              disabled={!!initialData.applicant.vin}
            />
          </Grid>
        </Grid>
      </FormSection>
      <FormSection title="personalClaim.personalData.applicantIs" icon={UserInfoIcon}>
        <Grid container spacing={4}>
          <Grid item md={12}>
            <ChangeApplicant
              primaryOption={{
                primaryLabel: 'personalClaim.personalData.victim',
                primaryValue: 'victim',
              }}
              value={applicantIs}
              onChange={(value) => {
                setApplicantIs(value);
                setValue('applicantIs', value);
              }}
            />
          </Grid>
        </Grid>
        {applicantIs === APPLICANT_TYPES[1].value && (
          <ClaimApplicantDetails
            initialData={initialData}
            formProps={{
              errors,
              clearError,
              getValues,
              setValue,
              register,
              unregister,
              triggerValidation,
            }}
            countries={countries}
            title={t('personalClaim.personalData.applicantDetails')}
          />
        )}
        {applicantIs === APPLICANT_TYPES[2].value && (
          <OtherLegalApplicantForm
            initialData={initialData}
            formProps={{
              register,
              setValue,
              errors,
              clearError,
              triggerValidation,
              unregister,
            }}
            valueNames={{
              hvhh: 'applicantOrganizationHvhh',
              organizationName: `applicantOrganizationName${formLanguage.toUpperCase()}`,
              email: 'applicantOrganizationEmail',
              phone: 'applicantOrganizationPhone',
            }}
          />
        )}
      </FormSection>
      <ApplicantResidenceAddress
        formProps={{
          register,
          getValues,
          setValue,
          errors,
          triggerValidation,
          clearError,
        }}
        applicantIs={applicantIs}
        previousApplicantState={previousApplicantState}
        initialData={{
          applicantResidenceCountry: getValues().applicantResidenceCountry || resCountry,
          applicantResidenceState: getValues().applicantResidenceState || resState,
          applicantResidenceCityVillage:
            getValues().applicantResidenceCityVillage || resCityVillage,
          applicantResidenceStreet: getValues().applicantResidenceStreet || resStreet,
        }}
      />
      <FormSection title="claim.personalData.participantIs" icon={UserInfoIcon}>
        <ChangeParticipant
          handleChange={handleChangeParticipant}
          participantIs={participantIs}
          applicantToVictim={getValues().applicantToVictim}
        />
        {participantIs === ACCIDENT_PARTICIPANT_TYPES[2].value && (
          <ClaimParticipantDetails
            initialData={initialData}
            formProps={{
              errors,
              clearError,
              getValues,
              setValue,
              register,
              unregister,
              triggerValidation,
            }}
            countries={countries}
          />
        )}
      </FormSection>
    </Box>
  );
};

LegalPersonalInformation.propTypes = {
  initialData: PropTypes.shape({
    hvhh: PropTypes.string,
    organizationName: PropTypes.string,
    stateNumber: PropTypes.string,
    transportManufacturer: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    transportModel: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
    vin: PropTypes.string,
    applicant: PropTypes.shape({
      organizationName: PropTypes.string,
      stateNumber: PropTypes.string,
      transportManufacturer: PropTypes.string,
      transportModel: PropTypes.string,
      vin: PropTypes.string,
      ownerSameInsured: PropTypes.string,
      ownerFirstName: PropTypes.string,
      ownerLastName: PropTypes.string,
      passportNum: PropTypes.string,
      ownerPassportNum: PropTypes.string,
      country: PropTypes.string,
      ownerCountry: PropTypes.string,
      state: PropTypes.string,
      ownerState: PropTypes.string,
      cityVillage: PropTypes.string,
      ownerCityVillage: PropTypes.string,
      street: PropTypes.string,
      ownerStreet: PropTypes.string,
      hvhh: PropTypes.string,
      ownerOrganizationName: PropTypes.string,
      orgName: PropTypes.string,
      ownerHvhh: PropTypes.string,
    }),
    applicantCitizenship: PropTypes.shape({}),
    applicantBDay: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    applicantFName: PropTypes.string,
    applicantLName: PropTypes.string,
    applicantPassNum: PropTypes.string,
    applicantPassGivenDate: PropTypes.instanceOf(Date),
    applicantPassValidUntilDate: PropTypes.instanceOf(Date),
    applicantPassFrom: PropTypes.string,
    applicantGender: PropTypes.number,
    participantIs: PropTypes.string,
    participantCitizenship: PropTypes.shape({}),
    participantBDay: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    participantFirstName: PropTypes.string,
    participantLastName: PropTypes.string,
    participantPassNum: PropTypes.string,
    participantPassGivenDate: PropTypes.instanceOf(Date),
    participantPassValidUntilDate: PropTypes.instanceOf(Date),
    participantPassFrom: PropTypes.string,
    applicantResidenceCountry: PropTypes.shape({}),
    applicantResidenceState: PropTypes.shape({}),
    applicantResidenceCityVillage: PropTypes.shape({}),
    applicantResidenceStreet: PropTypes.string,
    applicantIs: PropTypes.string,
  }),
  onFormSubmit: PropTypes.func.isRequired,
  goPrevious: PropTypes.func.isRequired,
  formLanguage: PropTypes.string.isRequired,
  setStepNavigationBox: PropTypes.func.isRequired,
};

LegalPersonalInformation.defaultProps = {
  initialData: {
    applicantIs: undefined,
  },
};

export default LegalPersonalInformation;
